import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ClipboardCheck, Eye, Search, Camera, CheckCircle2, User, MapPin, Calendar, StickyNote, Loader2,
} from "lucide-react";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const completionTone = (pct) => {
  if (pct >= 90) return "bg-green-100 text-green-700";
  if (pct >= 60) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const JobSheetsPanel = () => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/job-sheets`);
        if (!cancelled) setSheets(Array.isArray(data) ? data : []);
      } catch (err) {
        logError("Failed to load job sheets", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sheets;
    return sheets.filter((s) =>
      (s.cleaner_name || "").toLowerCase().includes(q) ||
      (s.property_address || "").toLowerCase().includes(q) ||
      (s.booking_ref || "").toLowerCase().includes(q)
    );
  }, [sheets, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="job-sheets-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#2D5016]" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="admin-job-sheets-panel"
    >
      <Card className="bg-white border-[#E2E8E0]">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-[#1A2E12] flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-[#2D5016]" />
              Cleaner Job Sheets ({sheets.length})
            </CardTitle>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B57]" />
              <Input
                placeholder="Search cleaner, address, ref…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="job-sheet-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-[#5C6B57]" data-testid="job-sheets-empty">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No job sheets submitted yet.</p>
              <p className="text-xs mt-1">
                Cleaners submit these from the Team Portal after every visit.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cleaner</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Booking ref</TableHead>
                    <TableHead>Visit date</TableHead>
                    <TableHead>Completion</TableHead>
                    <TableHead>Photos</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => {
                    const pct = s.total_items
                      ? Math.round((100 * (s.completed_count || 0)) / s.total_items)
                      : 0;
                    return (
                      <TableRow key={s.id} data-testid={`job-sheet-row-${s.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{s.cleaner_name || "—"}</span>
                            {s.cleaner_id ? (
                              <Badge className="bg-[#E8F5E9] text-[#2D5016] text-[10px] px-1.5 py-0" data-testid={`account-chip-${s.id}`}>
                                account
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0">
                                shared
                              </Badge>
                            )}
                          </div>
                          {s.cleaner_email && (
                            <div className="text-[11px] text-[#5C6B57]">{s.cleaner_email}</div>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate">
                          {s.property_address || "—"}
                        </TableCell>
                        <TableCell className="text-sm text-[#5C6B57]">
                          {s.booking_ref || "—"}
                        </TableCell>
                        <TableCell className="text-sm">{s.visit_date || "—"}</TableCell>
                        <TableCell>
                          <Badge className={completionTone(pct)}>
                            {s.completed_count || 0}/{s.total_items || 0} · {pct}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {s.photos_taken ? (
                            <Badge className="bg-green-100 text-green-700 gap-1">
                              <Camera className="w-3 h-3" /> Yes
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-[#5C6B57]">
                          {formatDate(s.submitted_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelected(s)}
                            data-testid={`view-job-sheet-${s.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="job-sheet-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1A2E12] flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-[#2D5016]" />
              Job Sheet Detail
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 text-[#2D5016] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase text-[#5C6B57] tracking-wider">Cleaner</div>
                    <div className="font-medium text-[#1A2E12]">{selected.cleaner_name || "—"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#2D5016] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase text-[#5C6B57] tracking-wider">Property</div>
                    <div className="font-medium text-[#1A2E12]">{selected.property_address || "—"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-[#2D5016] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase text-[#5C6B57] tracking-wider">Visit date</div>
                    <div className="font-medium text-[#1A2E12]">{selected.visit_date || "—"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#2D5016] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase text-[#5C6B57] tracking-wider">Completion</div>
                    <div className="font-medium text-[#1A2E12]">
                      {selected.completed_count || 0}/{selected.total_items || 0}
                      {selected.total_items ? ` · ${Math.round(100 * (selected.completed_count || 0) / selected.total_items)}%` : ""}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-[#E2E8E0]">
                <div>
                  <div className="text-xs uppercase text-[#5C6B57] tracking-wider">Booking ref</div>
                  <div className="text-[#1A2E12]">{selected.booking_ref || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-[#5C6B57] tracking-wider">Supervisor</div>
                  <div className="text-[#1A2E12]">{selected.supervisor_name || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-[#5C6B57] tracking-wider">Submitted</div>
                  <div className="text-[#1A2E12]">{formatDate(selected.submitted_at)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E2E8E0]">
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-[#5C6B57]" />
                  <span className="text-[#1A2E12]">
                    Photos taken: <strong>{selected.photos_taken ? "Yes" : "No"}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[#5C6B57]" />
                  <span className="text-[#1A2E12]">
                    Customer present: <strong>{selected.customer_present ? "Yes" : "No"}</strong>
                  </span>
                </div>
              </div>

              {selected.notes && (
                <div className="pt-2 border-t border-[#E2E8E0]">
                  <div className="text-xs uppercase text-[#5C6B57] tracking-wider mb-1 flex items-center gap-1">
                    <StickyNote className="w-3 h-3" /> Notes
                  </div>
                  <p className="text-[#1A2E12] whitespace-pre-wrap">{selected.notes}</p>
                </div>
              )}

              {Array.isArray(selected.completed_items) && selected.completed_items.length > 0 && (
                <div className="pt-2 border-t border-[#E2E8E0]">
                  <div className="text-xs uppercase text-[#5C6B57] tracking-wider mb-2">
                    Completed items ({selected.completed_items.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {selected.completed_items.map((id) => (
                      <span key={id} className="text-[11px] bg-[#E8F5E9] text-[#2D5016] px-2 py-0.5 rounded-full">
                        {id}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default JobSheetsPanel;
